import { useMemo } from 'react'
import type { AnalysisTimeRangeKey } from '../../../../types/analysis'
import type { WorkspacePulseEvent } from '../../../../services/aiAgentBridge'
import type { LiveModuleHeadline, LiveModuleRecord, LiveModuleSparkline } from '../LiveModuleCard'
import { bucketize, calculateDelta, timeRangeToDateRange } from './bucketize'

export interface UseLowConfidenceModuleParams {
  pulseEvents: WorkspacePulseEvent[]
  timeRange: AnalysisTimeRangeKey
  loading?: boolean
  error?: Error | null
  now?: Date
}

export interface UseLowConfidenceModuleResult {
  headline: LiveModuleHeadline
  sparkline: LiveModuleSparkline
  records: LiveModuleRecord[]
  loading: boolean
  error: Error | null
}

const parseDate = (val: unknown): Date | null => {
  if (!val) return null
  if (val instanceof Date) return val
  if (typeof (val as { toDate?: () => Date }).toDate === 'function') {
    return (val as { toDate: () => Date }).toDate()
  }
  if (typeof val === 'string' || typeof val === 'number') {
    const d = new Date(val)
    return isNaN(d.getTime()) ? null : d
  }
  return null
}

const isLowConfidenceEvent = (event: WorkspacePulseEvent): boolean => {
  const type = String(event.type || '')
  if (/LOW_CONFIDENCE|CLARIFICATION_REQUEST/i.test(type)) return true
  if (event.severity === 'CRITICAL' || event.severity === 'WATCH') {
    const summary = String(event.summary || '') + ' ' + String(event.title || '')
    return /confidence|clarification|uncertain|ambiguous/i.test(summary)
  }
  return false
}

export const useLowConfidenceModule = ({
  pulseEvents,
  timeRange,
  loading = false,
  error = null,
  now = new Date(),
}: UseLowConfidenceModuleParams): UseLowConfidenceModuleResult => {
  return useMemo(() => {
    const { from, to } = timeRangeToDateRange(timeRange, now)

    const inWindow = pulseEvents.filter((e) => {
      const d = parseDate(e.createdAt)
      return d && d >= from && d <= to && isLowConfidenceEvent(e)
    })

    const totalCount = inWindow.length

    const dates = inWindow
      .map((e) => parseDate(e.createdAt))
      .filter((d): d is Date => d !== null)

    const points = bucketize(dates, timeRange, now)
    const delta = calculateDelta(points)

    const topRecords: LiveModuleRecord[] = [...inWindow]
      .sort((a, b) => {
        const da = parseDate(a.createdAt)?.getTime() ?? 0
        const db = parseDate(b.createdAt)?.getTime() ?? 0
        return db - da
      })
      .slice(0, 5)
      .map((e, index) => {
        const title = e.title?.trim() || 'Low-confidence signal'
        const severity = e.severity || 'WATCH'
        const target = e.targetType || 'AI'
        return {
          id: e.id || `low-confidence-${index}`,
          primary: title,
          secondary: `${severity} · ${target}`,
          drillTarget: 'SUMMARY',
        }
      })

    const headline: LiveModuleHeadline = totalCount === 0
      ? { value: '—', label: 'no records' }
      : { value: totalCount, delta, label: 'signals' }

    return {
      headline,
      sparkline: { points, direction: 'up-is-bad' },
      records: topRecords,
      loading,
      error,
    }
  }, [pulseEvents, timeRange, loading, error, now])
}
